import { useLayoutEffect, type ReactNode } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'

/**
 * Route-level entrance. Wrap the routed view in this and key it on the pathname
 * so each view drifts gently in.
 *
 * There is deliberately no exit, and deliberately no `AnimatePresence` around
 * it. An exit animation under `mode="wait"` holds the OUTGOING page mounted
 * until it reports completion, and that report rides requestAnimationFrame —
 * which stops in a hidden document. A navigation in a backgrounded tab then
 * moved the URL and left the previous page rendered, with nothing to say so.
 * The outgoing page's fade was worth a fraction of a second of polish; it is
 * not worth making "which page am I on" an animation's decision.
 *
 * CSS, not framer-motion. A framer `y` animation is written from JavaScript on
 * every frame (Foundation research doc §14), so a route change — the moment the
 * main thread is busiest committing the new page — was exactly when the drift
 * stuttered. A keyframe plays on insertion with no script per frame, in every
 * WebView the shells target.
 */

// Transform-only: the page is readable on its first painted frame whether or
// not a second one comes. `translate`, not `transform`, so nothing the page sets
// on its own root is overridden; `backwards` so nothing is held after the end.
const StyledTransition = styled.div`
  width: 100%;
  /* Fills the scroller when the page is short and grows with it when it is
     long. The old flex: 1 + min-height: 100% capped this box at one screen with
     the page overflowing it, so the sticky PageHeader (whose containing block
     this is) scrolled away after the first screen. */
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  &[data-fill='true'] {
    flex: 1;
    min-height: 0;
  }
  animation: drift ${minimalVars.motion.slow} ${minimalVars.motion.easeEntrance} backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  :root:where([data-ui-tier="reduced_motion"]) & {
    animation: none;
  }

  @keyframes drift {
    from {
      translate: 0 12px;
    }
  }
`

export const PageTransition = ({
  children,
  className,
  fill = false,
}: {
  children: ReactNode
  className?: string
  fill?: boolean
}) => {
  // Keyed per route, so this runs exactly once per navigation: reset the shell
  // scroll container so the new page lands at the top.
  //
  // A LAYOUT effect, not a passive one. React runs a parent's passive effects
  // after its children's, so a plain useEffect here ran last and undid any
  // position the page itself had asked for — the conversation's "scroll to the
  // newest message" landed and was immediately reset, and reopening a chat put
  // the reader at the top of the history. Layout effects all run before any
  // passive effect, so the reset now comes first and the page gets the last word.
  useLayoutEffect(() => {
    const main = document.getElementById('main-content')
    if (!main) return
    try {
      main.scrollTo({ top: 0, left: 0 })
    } catch {
      main.scrollTop = 0
    }
  }, [])

  return <StyledTransition className={className} data-fill={fill}>{children}</StyledTransition>
}
